import { ArrowLeft, ChevronRight } from "lucide-react";
import SaveDraftButton from "../../../components/SaveDraftButton";
import {
  FormStack,
  PropertyStepActions,
} from "../../../components/propertyDescription/descriptionUi";
import {
  NextButton,
  RegisterButton,
} from "../../CreateProperty/twStyles";
import AccommodationsSection from "../../CreateProperty/components/DescriptionForm/sections/AccommodationsSection";
import CondominiumSection from "../../CreateProperty/components/DescriptionForm/sections/CondominiumSection";
import BenefitsSection from "../../CreateProperty/components/CharacteristicsForm/BenefitsSection";
import { useEditPropertyWizard } from "./EditPropertyWizardContext";
import AmenitiesSection from "./description/AmenitiesSection";

const BENEFIT_SETTERS: Record<string, string> = {
  is_nearby_clinics: "setIsNearbyClinics",
  is_nearby_major_roads: "setIsNearbyMajorRoads",
  is_nearby_schools: "setIsNearbySchools",
  is_nearby_mall: "setIsNearbyMall",
  is_nearby_public_transport: "setIsNearbyPublicTransport",
  has_24h_concierge: "setHas24hConcierge",
  has_ac: "setHasAC",
  has_backyard: "setHasBackyard",
  has_bbq: "setHasBBQ",
  has_cable_tv: "setHasCableTV",
  has_elevator: "setHasElevator",
  has_game_room: "setHasGameRoom",
  has_garden: "setHasGarden",
  has_gourmet: "setHasGourmet",
  has_gourmet_porch: "setHasGourmetPorch",
  has_gym: "setHasGym",
  has_home_cinema: "setHasHomeCinema",
  has_party: "setHasParty",
  has_playground: "setHasPlayground",
  has_pool: "setHasPool",
  has_porch: "setHasPorch",
  has_sauna: "setHasSauna",
  has_sea_view: "setHasSeaView",
  has_spa: "setHasSPA",
  has_sports: "setHasSports",
  has_storage: "setHasStorage",
  has_tennis_court: "setHasTennisCourt",
};

const BENEFIT_GETTERS: Record<string, string> = {
  is_nearby_clinics: "isNearbyClinics",
  is_nearby_major_roads: "isNearbyMajorRoads",
  is_nearby_schools: "isNearbySchools",
  is_nearby_mall: "isNearbyMall",
  is_nearby_public_transport: "isNearbyPublicTransport",
  has_24h_concierge: "has24hConcierge",
  has_ac: "hasAC",
  has_backyard: "hasBackyard",
  has_bbq: "hasBBQ",
  has_cable_tv: "hasCableTV",
  has_elevator: "hasElevator",
  has_game_room: "hasGameRoom",
  has_garden: "hasGarden",
  has_gourmet: "hasGourmet",
  has_gourmet_porch: "hasGourmetPorch",
  has_gym: "hasGym",
  has_home_cinema: "hasHomeCinema",
  has_party: "hasParty",
  has_playground: "hasPlayground",
  has_pool: "hasPool",
  has_porch: "hasPorch",
  has_sauna: "hasSauna",
  has_sea_view: "hasSeaView",
  has_spa: "hasSPA",
  has_sports: "hasSports",
  has_storage: "hasStorage",
  has_tennis_court: "hasTennisCourt",
};

export default function EditCharacteristicsStep() {
  const wizard = useEditPropertyWizard();
  const {
    handleGoToPhotosForm,
    handleSaveCharacteristicsDraft,
    handleDraftNextFromCharacteristics,
    isDraft,
    isSavingDraft,
    loadingRequest,
    setFormOptionSelected,
    rooms,
    setRooms,
    suites,
    setSuites,
    bathrooms,
    setBathrooms,
    garage,
    setGarage,
    condominium,
    setCondominium,
  } = wizard;

  const benefitValues = Object.entries(BENEFIT_GETTERS).reduce(
    (acc, [attribute, stateKey]) => {
      acc[attribute] = Boolean(wizard[stateKey]);
      return acc;
    },
    {} as Record<string, boolean>
  );

  function handleBenefitChange(attributeName: string, value: boolean) {
    const setterName = BENEFIT_SETTERS[attributeName];
    wizard[setterName]?.(value ? 1 : 0);
  }

  function handleAccommodationChange(field: string, value: unknown) {
    if (field === "rooms") setRooms(value);
    if (field === "suites") setSuites(value);
    if (field === "bathrooms") setBathrooms(value);
    if (field === "garages") setGarage(value);
  }

  return (
    <div className="w-full min-w-0">
      <FormStack>
        <AccommodationsSection
          rooms={rooms}
          suites={suites}
          bathrooms={bathrooms}
          garages={garage}
          onChange={handleAccommodationChange}
        />
        <AmenitiesSection />
        <CondominiumSection
          condominium={condominium}
          onChange={setCondominium}
        />
        <BenefitsSection values={benefitValues} onChange={handleBenefitChange} />
      </FormStack>

      <PropertyStepActions
        leading={
          isDraft ? (
            <SaveDraftButton
              isLoading={isSavingDraft}
              onClick={handleSaveCharacteristicsDraft}
            >
              Salvar rascunho
            </SaveDraftButton>
          ) : (
            <RegisterButton
              type="button"
              className="h-10 py-0"
              onClick={loadingRequest ? () => {} : handleGoToPhotosForm}
            >
              <p>{loadingRequest ? "Carregando..." : "Salvar"}</p>
            </RegisterButton>
          )
        }
        trailing={
          <>
            <NextButton
              type="button"
              className="inline-flex h-10 gap-1.5 py-0 [&>svg]:!ml-0"
              onClick={() => {
                setFormOptionSelected("publication");
                window.scrollTo(0, 0);
              }}
            >
              <ArrowLeft className="h-4 w-4 text-white" aria-hidden />
              <p>Voltar</p>
            </NextButton>
            <NextButton
              type="button"
              className="inline-flex h-10 gap-1.5 py-0"
              onClick={() => {
                if (isDraft) {
                  handleDraftNextFromCharacteristics?.();
                  return;
                }
                setFormOptionSelected("photos");
                window.scrollTo(0, 0);
              }}
            >
              <p>Próximo</p>
              <ChevronRight className="h-4 w-4 text-white" aria-hidden />
            </NextButton>
          </>
        }
      />
    </div>
  );
}
